// @flow

import type { UserOperation } from 'fc-operator-user/types'

// Libs
import React, { Component } from 'react'
import _find from 'lodash/find'
import _trim from 'lodash/trim'
import _isEmpty from 'lodash/isEmpty'
import _get from 'lodash/get'
import validator from 'validator'

// Components
import Button from '@react-mdc/button'
import { Errors } from 'hfty-components-errors'
import { FormattedMessage } from 'react-intl'

// Messages
import messages from '../messages'
import accountsMessages from 'hfty-accounts/src/messages'

// Constants
import { PROP_FIRST, PROP_LAST, PROP_USERNAME, PROP_PARENT, PROP_ID } from 'fc-model-users'

// Components
import Textfield from '@react-mdc/textfield'
import FormField from '@react-mdc/form-field'

// Styles
import { sidebar as sidebarCommonStyle, INDENT } from 'hfty-styles/src/common'
import { error, black } from 'hfty-styles/src/colors'
const sidebar = sidebarCommonStyle

const styles = {
  passwordError: {
    color: error(),
  },
  caseSensitiveText: {
    color: black(),
    fontSize: '12px',
    padding: '3px',
    fontWeight: 500,
  },
  buttonWrapper: {
    marginTop: INDENT,
  },
  formField: {
    width: '100%',
  },
  textField: {
    width: '95%',
  },
}

type Props = {
  operation: UserOperation,
  onChange: Function,
  onCancel: Function,
  onSubmit: Function,
  authenticatedUser: Object,
  users: Array,
}

export default class AddUser extends Component {
  props: Props
  onChangeFirst: Function
  onChangeLast: Function
  onSubmit: Function
  onCancel: Function

  constructor(props: Props) {
    super(props)

    this.state = {
      password: '',
      confirmPassword: '',
      emailError: null,
      triedSubmission: false,
    }

    this.onChangeFirst = this.onChangeFirst.bind(this)
    this.onChangeLast = this.onChangeLast.bind(this)
    this.onChangePhone = this.onChangePhone.bind(this)
    this.onChangeEmail = this.onChangeEmail.bind(this)
    this.onChangeUsername = this.onChangeUsername.bind(this)
    this.onChangePassword = this.onChangePassword.bind(this)
    this.onChangeConfirmPassword = this.onChangeConfirmPassword.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.isSubmitDisabled = this.isSubmitDisabled.bind(this)
    this.checkExistingEmails = this.checkExistingEmails.bind(this)
  }

  isSubmitDisabled(): boolean {
    const email = _get(this.props.operation, 'model.email', '')
    const first = _get(this.props.operation, 'model.first', '')
    const last = _get(this.props.operation, 'model.last', '')
    const username = _get(this.props.operation, 'model.username', '')
    const phone = _get(this.props.operation, 'model.phone', '')

    return (
      _isEmpty(this.state.password) ||
      _isEmpty(this.state.confirmPassword) ||
      _isEmpty(first) ||
      _isEmpty(last) ||
      _isEmpty(username) ||
      _isEmpty(email) ||
      _isEmpty(phone)
    )
  }

  checkExistingEmails(email: string): boolean {
    return _find(this.props.users, { email }) !== undefined
  }

  onSubmit(e: Event) {
    e.preventDefault()
    this.setState({
      triedSubmission: true,
    })
    const email = this.props.operation.model.email
    if (email && !validator.isEmail(email)) {
      this.setState({
        emailError: 'Invalid email address',
      })
    } else if (this.checkExistingEmails(email)) {
      this.setState({
        emailError: 'This email address is already in use',
      })
    } else {
      this.setState({
        emailError: null,
      })

      let passwd = this.state.password ? this.state.password : null
      if (passwd) {
        if (passwd !== this.state.confirmPassword) {
          this.setState({
            passwordError: 'Passwords do not match',
          })
        } else if (passwd.length < 8) {
          this.setState({
            passwordError: 'Password must be at least 8 characters',
          })
        } else {
          this.setState({
            passwordError: null,
          })
          this.props.operation.model[PROP_PARENT] = this.props.authenticatedUser[PROP_ID]
          const finalOperation = {
            ...this.props.operation,
            model: {
              ...this.props.operation.model,
              user: {
                ...this.props.operation.model.user,
                password: passwd,
              },
            },
          }
          this.props.onSubmit([finalOperation])
        }
      } else {
        this.setState({
          passwordError: 'Password cannot be blank',
        })
      }
    }
  }

  onCancel(e: Event) {
    e.preventDefault()

    this.setState({
      password: '',
      confirmPassword: '',
      emailError: null,
      triedSubmission: false,
    })

    this.props.onCancel([this.props.operation])
  }

  onChangeFirst(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        [PROP_FIRST]: e.currentTarget.value,
      },
    })
  }

  onChangeLast(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        [PROP_LAST]: e.currentTarget.value,
      },
    })
  }

  onChangePhone(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        phone: e.currentTarget.value,
      },
    })
  }

  onChangeEmail(e: Event & { currentTarget: HTMLInputElement }) {
    const value = _trim(e.currentTarget.value || '')
    const isValidEmail = value === '' || validator.isEmail(value)
    if (!isValidEmail) {
      this.setState({
        emailError: 'Invalid email address',
      })
    } else {
      this.setState({
        emailError: null,
      })
    }
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        email: value,
      },
    })
  }

  onChangeUsername(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_USERNAME]: e.currentTarget.value,
        user: {
          ...this.props.operation.model.user,
          [PROP_USERNAME]: e.currentTarget.value,
        },
      },
    })
  }

  onChangePassword(e: Event & { currentTarget: HTMLInputElement }) {
    this.setState({
      password: e.currentTarget.value,
    })
  }

  onChangeConfirmPassword(e: Event & { currentTarget: HTMLInputElement }) {
    this.setState({
      confirmPassword: e.currentTarget.value,
    })
  }

  renderUsernameisCaseSentiveText(): React.Element<any> {
    return (
      <div style={styles.caseSensitiveText}>
        <FormattedMessage {...accountsMessages['usernameIsCaseSensitive']} />
      </div>
    )
  }

  render(): React.Element<any> {
    if (_isEmpty(this.props.operation)) {
      return null
    }

    return (
      <form style={sidebar.container} onSubmit={this.onSubmit}>
        <FormField style={styles.formField}>
          <Errors errors={this.props.operation.errors} messages={messages} />
        </FormField>
        <FormField style={styles.formField}>
          <Textfield disabled={this.props.operation.submitting} style={styles.textField}>
            <Textfield.Input id={`first-${this.props.operation.operationId}`} onChange={this.onChangeFirst} required />
            <Textfield.Label htmlFor="usage-1">{'First Name'}</Textfield.Label>
          </Textfield>
        </FormField>
        <FormField style={styles.formField}>
          <Textfield disabled={this.props.operation.submitting} style={styles.textField}>
            <Textfield.Input id={`last-${this.props.operation.operationId}`} onChange={this.onChangeLast} required />
            <Textfield.Label htmlFor="usage-1">{'Last Name'}</Textfield.Label>
          </Textfield>
        </FormField>
        <FormField style={styles.formField}>
          <Textfield disabled={this.props.operation.submitting} style={styles.textField}>
            <Textfield.Input id={`phone-${this.props.operation.operationId}`} onChange={this.onChangePhone} required />
            <Textfield.Label htmlFor="usage-1">{'Phone'}</Textfield.Label>
          </Textfield>
        </FormField>
        <FormField style={styles.formField}>
          <Textfield disabled={this.props.operation.submitting} style={styles.textField}>
            <Textfield.Input id={`email-${this.props.operation.operationId}`} onChange={this.onChangeEmail} required />
            <Textfield.Label htmlFor="usage-1">{'Email'}</Textfield.Label>
          </Textfield>
        </FormField>
        {this.state.triedSubmission && <div style={styles.passwordError}>{this.state.emailError}</div>}
        <FormField style={styles.formField}>
          <Textfield disabled={this.props.operation.submitting} style={styles.textField}>
            <Textfield.Input
              id={`username-${this.props.operation.operationId}`}
              onChange={this.onChangeUsername}
              required
            />
            <Textfield.Label htmlFor="usage-1">{'Username'}</Textfield.Label>
          </Textfield>
        </FormField>
        {this.renderUsernameisCaseSentiveText()}
        <FormField style={styles.formField}>
          <Textfield disabled={this.props.operation.submitting} style={styles.textField}>
            <Textfield.Input
              id={`password-${this.props.operation.operationId}`}
              onChange={this.onChangePassword}
              type={'password'}
              required
            />
            <Textfield.Label htmlFor="usage-1">{'Password'}</Textfield.Label>
          </Textfield>
        </FormField>
        <div style={styles.passwordError}>{this.state.passwordError}</div>
        <FormField style={styles.formField}>
          <Textfield disabled={this.props.operation.submitting} style={styles.textField}>
            <Textfield.Input
              id={`confirmPassword-${this.props.operation.operationId}`}
              onChange={this.onChangeConfirmPassword}
              type={'password'}
              required
            />
            <Textfield.Label htmlFor="usage-1">{'Confirm Password'}</Textfield.Label>
          </Textfield>
        </FormField>
        <div style={{ ...sidebar.buttonWrapper, ...styles.buttonWrapper }}>
          <Button raised primary style={sidebar.button} disabled={this.isSubmitDisabled()} onClick={this.onSubmit}>
            {'Submit'}
          </Button>
          <Button raised onClick={this.onCancel}>
            {'Cancel'}
          </Button>
        </div>
      </form>
    )
  }
}
